import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useInvite } from "@/hooks/useInvite";
import { candidateApi } from "@/lib/candidateApi";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Spinner, Alert } from "@/components/primitives";
import { InviteError } from "./InviteError";
import { consentStorageKey } from "./InviteLanding";

const LEN = 6;

// Six-box OTP input. Behaviour ported from invite_verify.html's inline script:
// auto-advance, backspace-to-previous, paste-fills-all, auto-submit when full.
function OtpBoxes({
  value,
  onChange,
  onComplete,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete: (v: string) => void;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(LEN, " ").slice(0, LEN).split("");

  function setAt(i: number, ch: string) {
    const next = (value.padEnd(LEN, " ").slice(0, LEN).split("") as string[]);
    next[i] = ch;
    const joined = next.join("").replace(/ /g, "");
    onChange(joined);
    if (ch && i < LEN - 1) refs.current[i + 1]?.focus();
    if (joined.length === LEN) onComplete(joined);
  }

  return (
    <div className="flex justify-between gap-2" onPaste={(e) => {
      const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LEN);
      if (text.length >= LEN) {
        e.preventDefault();
        onChange(text);
        onComplete(text);
        refs.current[LEN - 1]?.blur();
      }
    }}>
      {Array.from({ length: LEN }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          disabled={disabled}
          aria-label={`Digit ${i + 1}`}
          value={digits[i].trim()}
          onChange={(e) => {
            const ch = e.target.value.replace(/\D/g, "").slice(-1);
            setAt(i, ch);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !digits[i].trim() && i > 0) {
              refs.current[i - 1]?.focus();
            }
          }}
          className="h-14 w-12 rounded-md border border-hair bg-paper-2 text-center font-mono text-2xl font-semibold text-ink outline-none focus:border-accent disabled:opacity-50"
        />
      ))}
    </div>
  );
}

export function OtpVerify() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data, error: inviteError, loading } = useInvite(token);

  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fallback, setFallback] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  // A returning candidate may have agreed to a changed privacy notice on the
  // landing page before they could be verified; record it on the server once
  // they are, on every route that verifies them.
  const syncPendingConsent = useCallback(async () => {
    if (!token) return;
    const key = consentStorageKey(token);
    const version = data?.privacyNoticeVersion;
    try {
      if (key && version && data?.privacyAcceptedVersion !== version
          && window.localStorage.getItem(key) === version) {
        const saved = await candidateApi.recordPrivacyConsent(token, version);
        if (saved.ok) window.localStorage.removeItem(key);
      }
    } catch {
      // The local copy stays as the fallback record.
    }
  }, [token, data]);

  const send = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    const r = await candidateApi.sendOtp(token);
    setBusy(false);
    if (r.verified) {
      await syncPendingConsent();
      // Honour the server's routing. It answers "home" for a returning invite,
      // and /setup 410s on a review-only one, so hardcoding it dead-ended a
      // candidate who was already signed in. Codex review, 2026-09-02.
      navigate(`/invite/${token}/${r.next ?? "setup"}`);
      return;
    }
    if (!r.ok) {
      setErr(r.error ?? "Could not send the code. Please try again.");
      if (r.cooldown) setCooldown(r.cooldown);
      return;
    }
    setSent(true);
    setFallback(r.fallbackCode ?? null);
    setCooldown(r.cooldown ?? 30);
  }, [token, navigate, syncPendingConsent]);

  const verify = useCallback(
    async (fullCode: string) => {
      if (!token || fullCode.length !== LEN) return;
      setBusy(true);
      setErr(null);
      const r = await candidateApi.verifyOtp(token, fullCode);
      setBusy(false);
      if (r.ok && r.verified) {
        await syncPendingConsent();
        // Route new candidates through the personalisation quiz (C4) before setup.
        // Returning candidates (r.next === "home") skip to home directly.
        const nextStep = r.next === "home" ? "home" : "personalise";
        navigate(`/invite/${token}/${nextStep}`);
        return;
      }
      setErr(r.error ?? "Incorrect code. Please try again.");
      setCode("");
    },
    [token, navigate, syncPendingConsent],
  );

  if (loading) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }
  if (inviteError || !data) return <InviteError message={inviteError ?? "Invite not found."} />;

  return (
    <CandidateFlow agencyName={data.agencyName} logoUrl={data.branding?.logoUrlLight || undefined} logoFilter={data.branding?.logoFilter}>
      <Link to={`/invite/${token}`} className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink">
        <span aria-hidden="true">←</span> Back
      </Link>

      <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-md bg-accent-50 text-accent">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M4 7l8 6 8-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>

      <h1 className="text-xl font-semibold tracking-[-0.3px] text-ink">Verify your email</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">
        We'll send a 6-digit code to <strong className="text-ink-2">{data.candidateEmail}</strong> to
        confirm your identity.
      </p>

      {err && <Alert className="mt-4">{err}</Alert>}

      {!sent ? (
        <Button variant="primary" className="mt-5 w-full" onClick={send} disabled={busy}>
          {busy ? <Spinner size={16} /> : "Send verification code"}
        </Button>
      ) : (
        <div className="mt-5 space-y-4">
          {fallback ? (
            <Alert variant="warn">
              <strong>Email delivery failed.</strong> Your code is:{" "}
              <span className="font-mono text-lg font-bold tracking-[6px]">{fallback}</span>
              <div className="mt-1 text-xs">Enter this code in the boxes below to continue.</div>
            </Alert>
          ) : (
            <p className="text-sm text-slate-500">
              Code sent to <strong className="text-ink-2">{data.candidateEmail}</strong>
            </p>
          )}

          <OtpBoxes value={code} onChange={setCode} onComplete={verify} disabled={busy} />

          <Button variant="primary" className="w-full" disabled={busy || code.length !== LEN} onClick={() => verify(code)}>
            {busy ? <Spinner size={16} /> : "Verify code"}
          </Button>

          <div className="text-center text-sm text-slate-500">
            {cooldown > 0 ? (
              <span>Resend available in {cooldown}s</span>
            ) : (
              <button type="button" className="font-medium text-accent hover:underline" onClick={send} disabled={busy}>
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
    </CandidateFlow>
  );
}
